import React from 'react';
import * as S from './SuitabilityTest.style';
import SuitabilityTestInputs from './SuitabilityTestInputs';
import { useState, useEffect, useCallback } from 'react';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { useHistory } from 'react-router-dom';
import SavingsPlanService, { SavingsPlanType } from '@/services/SavingsPlanService';
import UserIdentitiesService, { UserIdentityType } from '@/services/UserIdentitiesService';
import SuitabilityTestsService,
{
    ISuitabilityTestVersion,
    ISuitabilityTest,
    IQuestion,
    IQuestionTableRow,
    IAnswer,
    IBlock,
    ISubBlock,
    IQuestionAnswer
} from '@/services/SuitabilityTestsService';
import { buildAnswersForSubmit, buildMissingRadioAnswers, buildSuitabilityTestForm, prepareSuitabilityInitialAnswers } from './suitabilityTestInitialAnswers';
import { selectDraftSuitabilityTest } from './selectDraftSuitabilityTest';
import { isSuitabilityLimitReachedError, resolveSuitabilityTestError } from './suitabilityTestErrorMessages';

export interface SuitabilityTestProps
{
    /** La contratación y los productos complejos lo siguen pasando. La firma va por interviniente. */
    savingsPlanId?: number;
    userIdentityId: number;
    confirmUrl: string;
    /** Respuestas del test anterior ya casadas con la versión actual. Sin esta prop el formulario arranca vacío. */
    initialAnswers?: IAnswer[];
    /** 409 de los 30 días. La renovación vuelve al listado; la contratación lo deja en el formulario. */
    onLimitReached?: (message: string) => void;
}

const SuitabilityTest = ({ userIdentityId, confirmUrl, initialAnswers, onLimitReached }: SuitabilityTestProps) =>
{
    const history = useHistory();
    const [isLoaded, setIsLoaded] = useState(false);
    const [isLoadedSuitabilityTestVersion, setIsLoadedSuitabilityTestVersion] = useState(false);
    const [userIdentity, setUserIdentity] = useState<UserIdentityType>();
    const [savingsPlan, setSavingsPlan] = useState<SavingsPlanType>();
    const [suitabilityTestVersion, setSuitabilityTestVersions] = useState<ISuitabilityTestVersion>();
    const [suitabilityTest, setSuitabilityTest] = useState<ISuitabilityTest>();
    const [error, setError] = useState({ status: false, message: '' });
    const [suitabilityTestForm, setSuitabilityTestForm] = useState<any>();
    const [questions, setQuestions] = useState<IQuestion[]>();
    const [isAnswersLoaded, setIsAnswersLoaded] = useState(false);
    const [canSubmit, setCanSubmit] = useState(false);

    const reportRequestError = (requestError: unknown): void =>
    {
        const message = resolveSuitabilityTestError(requestError);
        if(isSuitabilityLimitReachedError(requestError) && onLimitReached)
        {
            onLimitReached(message);
            setIsLoaded(true);
            return;
        }

        setError({ status: true, message: message });
        setIsLoaded(true);
    };

    useEffect(() =>
    {
        SuitabilityTestsService.getLastSuitabilityTestVersion().then((suitabilityTestVersion: ISuitabilityTestVersion) =>
        {
            setSuitabilityTestVersions(suitabilityTestVersion);
            setIsLoadedSuitabilityTestVersion(true);
        },
        (requestError) =>
        {
            setError({ status: true, message: resolveSuitabilityTestError(requestError) });
            setIsLoadedSuitabilityTestVersion(true);
        });
    }, []);

    useEffect(() => {
        if(userIdentityId !== undefined) {
            UserIdentitiesService.getUserIdentity(userIdentityId)
                .then((userIdentity: UserIdentityType) => {
                    setUserIdentity(userIdentity);
                    setIsLoaded(true);
                },
                (requestError: unknown) =>
                {
                    reportRequestError(requestError);
                });
        }
    }, [userIdentityId]);

    useEffect(() =>
    {
        if(suitabilityTestVersion && userIdentity !== undefined)
        {
            var subBlocks = suitabilityTestVersion.test.blocks.map((block: IBlock) => block.subBlock).reduce((prev?: ISubBlock[], next?: ISubBlock[]) =>
            {
                return (prev !== undefined ? (next !== undefined ? prev?.concat(next) : prev) : (next !== undefined ? next : []))
            });

            var questions = subBlocks.map((subBlock: ISubBlock) => subBlock.questions).reduce((prev?: IQuestion[], next?: IQuestion[]) =>
            {
                return (prev !== undefined ? (next !== undefined ? prev?.concat(next) : prev) : (next !== undefined ? next : []))
            });

            setQuestions(questions);
        }
    }, [suitabilityTestVersion, userIdentity]);

    useEffect(() =>
    {
        if(questions !== undefined
            && questions.length > 0
            && suitabilityTestVersion !== undefined
            && userIdentity !== undefined
        ) {
            // initialAnswers llega resuelto antes de montar el cuestionario. No va en las dependencias:
            // si el padre recrea el array, no hay que vaciar lo que el usuario ya haya cambiado.
            const preparedAnswers = initialAnswers === undefined
                ? undefined
                : prepareSuitabilityInitialAnswers(questions, initialAnswers);
            const form = buildSuitabilityTestForm(questions, preparedAnswers);
            setSuitabilityTestForm(form);
            if(preparedAnswers !== undefined)
            {
                setCanSubmit(Object.values(form).every((isValid) => isValid === true));
            }

            setSuitabilityTest({
                suitabilityTestVersionId: suitabilityTestVersion.id,
                userIdentityId: userIdentity.id,
                answers: preparedAnswers ?? [],
                status: "DRAFT"
            });
            setIsLoaded(true);
        }
    }, [suitabilityTestVersion, questions]);

    useEffect(() =>
    {
        if(!isAnswersLoaded
            && suitabilityTest !== undefined
            && suitabilityTest
            && questions !== undefined
        ) {
            const currentQuestions = questions;
            setSuitabilityTest((prevState) =>
            {
                if(!prevState)
                {
                    return prevState;
                }

                const missing = buildMissingRadioAnswers(currentQuestions, prevState.answers ?? []);
                if(missing.length === 0)
                {
                    return prevState;
                }

                return {
                    ...prevState,
                    answers: [
                        ...(prevState.answers ?? []),
                        ...missing
                    ]
                };
            });
            setIsAnswersLoaded(true);
        }
    }, [suitabilityTest, questions]);

    const handleTestChange = (event: any, question: IQuestion) =>
    {
        const { name, value }: { name: string, value: string } = event.target;

        var answers: IAnswer[] | undefined = [];
        var answer: IAnswer | undefined;
        var questionAnswer: IQuestionAnswer | undefined;
        switch(question.questionType)
        {
            case "radio":
                answers = suitabilityTest?.answers?.filter((answerAux: IAnswer) => answerAux.questionId !== question._id);
                answer = Object.assign({}, suitabilityTest?.answers?.find((answerAux: IAnswer) => answerAux.questionId === question._id));
                const order = Number(event.target.dataset.order);
                questionAnswer = question?.answers.find((answerAux: IQuestionAnswer) => answerAux.value === Number(value) && answerAux.order === order);

                if(answer !== undefined && questionAnswer !== undefined)
                {
                    answer.res = questionAnswer.value;
                    answer.order = questionAnswer.order.toString();

                    if (question.product !== null) {
                        answer.product = question.product;
                    }

                    if (question.constraintKey !== null) {
                        answer.constraintKey = question.constraintKey;
                    }

                    if (question.secondaryKey !== null) {
                        answer.secondaryKey = question.secondaryKey;
                    }

                    setSuitabilityTest((prevState: any) => (
                        {
                            ...prevState,
                            answers: [
                                ...prevState.answers.filter((answerAux: IAnswer) => answerAux.questionId !== question._id),
                                answer
                            ]
                        }
                    ));
                }

                break;

            case "checkbox":
                var [questionId, answerId] = name.split("-");
                questionAnswer = question?.answers.find((answerAux: IQuestionAnswer) => answerAux._id === answerId);
                if(questionAnswer !== undefined)
                {
                    var answersAux = suitabilityTest?.answers?.map((answer: IAnswer) => Object.assign({}, answer)) || [];
                    if(event.target.checked)
                    {
                        answersAux.push(
                        {
                            questionId: questionId,
                            key: question.key,
                            order: questionAnswer.order.toString(),
                            res: questionAnswer.value,
                            product: question.product
                        });
                    }
                    else
                    {
                        var answerIndex = answersAux.findIndex((answerAux: IAnswer) => answerAux.questionId === questionId && answerAux.order === questionAnswer?.order && answerAux.value === questionAnswer?.value);
                        if(answerIndex !== undefined)
                        {
                            answersAux.splice(answerIndex, 1);
                        }
                    }

                    setSuitabilityTest((prevState: any) => (
                        {
                            ...prevState,
                            answers: answersAux
                        }
                    ));
                }
                break;

            case "multi":
                var [questionIdMulti, answerId] = name.split("-");
                questionAnswer = question?.answers.find((answerAux: IQuestionAnswer) => answerAux._id === answerId);

                if(questionAnswer !== undefined)
                {
                    if(event.target.hasOwnProperty("checked")) {
                        var answersAux = suitabilityTest?.answers || [];
                        if(event.target.checked) {
                            var answerAux: IAnswer = {
                                questionId: question._id,
                                answerId: questionAnswer._id,
                                key: question.key,
                                value: questionAnswer.value,
                                res: questionAnswer.order
                            };
                            if (question.constraintKey !== null) {
                                answerAux.constraintKey = question.constraintKey;
                            }
                            answersAux.push(answerAux);
                        }
                        else {
                            var answerIndex = answersAux.findIndex((answerAux: IAnswer) => answerAux.questionId === answerId);
                            if(answerIndex !== undefined) {
                                answersAux.splice(answerIndex, 1);
                            }
                        }

                        setSuitabilityTest((prevState: any) => (
                            {
                                ...prevState,
                                answers: answersAux
                            }
                        ));
                    }
                    else {
                        answers = suitabilityTest?.answers?.filter((answer: IAnswer) => answer.answerId !== answerId);
                        answer = suitabilityTest?.answers?.find((answer: IAnswer) => answer.answerId === answerId);

                        if(answers !== undefined && answer !== undefined && questionAnswer !== undefined) {
                            setSuitabilityTest((prevState: any) => {
                                if(answers !== undefined && questionAnswer !== undefined) {
                                    return {
                                        ...prevState,
                                        answers: [
                                            ...answers,
                                            {
                                                ...answer,
                                                inputRes: parseFloat(value),
                                                value: questionAnswer.value * parseFloat(value) / 100
                                            }
                                        ]
                                    }
                                }
                                else {
                                    return prevState;
                                }
                            });
                        }
                    }
                }
                break;

            case "table":
                var [questionId, rowId, colIndex] = name.split("-");
                var questionRow = question?.table.rows.find((row: IQuestionTableRow) => row._id === rowId);
                if(questionRow !== undefined)
                {
                    answersAux = suitabilityTest?.answers?.map((answer: IAnswer) => Object.assign({}, answer)) || [];
                    if(event.target.checked)
                    {
                        if(colIndex === "1")
                        {
                            answersAux.push({
                                originalQuestionId: questionId,
                                questionId: questionRow.cols[parseInt(colIndex)]._id,
                                res: questionRow.cols[parseInt(colIndex)].value,
                                colKey: questionRow.cols[parseInt(colIndex)].productKey,
                                colOrder: questionRow.cols[parseInt(colIndex)].order,
                                key: question?.table.key,
                                secondaryKey: question?.table.secondaryKey
                            });
                        }
                        else
                        {
                            if(!answersAux.find((answerAux: IAnswer) => questionRow !== undefined && answerAux.questionId === questionRow.cols[1]._id))
                            {
                                answersAux.push({
                                    originalQuestionId: questionId,
                                    questionId: questionRow.cols[1]._id,
                                    res: questionRow.cols[1].value,
                                    colKey: questionRow.cols[1].productKey,
                                    colOrder: questionRow.cols[1].order,
                                    key: question?.table.key,
                                    secondaryKey: question?.table.secondaryKey
                                });
                            }

                            answersAux.push({
                                originalQuestionId: questionId,
                                questionId: questionRow.cols[2]._id,
                                res: questionRow.cols[2].value,
                                colKey: questionRow.cols[2].productKey,
                                colOrder: questionRow.cols[2].order,
                                key: question?.table.key,
                                secondaryKey: question?.table.secondaryKey
                            });
                        }
                    }
                    else
                    {
                        if(colIndex === "1")
                        {
                            var answerIndex = answersAux.findIndex((answerAux: IAnswer) => questionRow !== undefined && answerAux.questionId === questionRow.cols[1]._id);
                            if(answerIndex !== undefined)
                            {
                                answersAux.splice(answerIndex, 1);
                            }

                            answerIndex = answersAux.findIndex((answerAux: IAnswer) => questionRow !== undefined && answerAux.questionId === questionRow.cols[2]._id);
                            if(answerIndex !== -1)
                            {
                                answersAux.splice(answerIndex, 1);
                            }
                        }
                        else
                        {
                            var answerIndex = answersAux.findIndex((answerAux: IAnswer) => questionRow !== undefined && answerAux.questionId === questionRow.cols[2]._id);
                            if(answerIndex !== -1)
                            {
                                answersAux.splice(answerIndex, 1);
                            }
                        }
                    }

                    setSuitabilityTest((prevState: any) => (
                        {
                            ...prevState,
                            answers: answersAux
                        }
                    ));
                }
                break;
        }
    };

    const btnSubmit = useCallback(() =>
    {
        if(suitabilityTestForm !== undefined)
        {
            setCanSubmit(Object.values(suitabilityTestForm).every((x) => x === true));
        }
    }, [suitabilityTestForm]);

    const updateSuitabilityTestForm = (name: string, value: any) =>
    {
        suitabilityTestForm[name] = value;
        setSuitabilityTestForm(suitabilityTestForm);
        btnSubmit();
    }

    const handleSubmit = (event: any) =>
    {
        event.preventDefault();
        if(suitabilityTest !== undefined) {
            setError({ status: false, message: "" });
            setIsLoaded(false);
            SuitabilityTestsService.createSuitabilityTest({ ...suitabilityTest, answers: buildAnswersForSubmit(questions, suitabilityTest.answers) })
                .then((createdTest: ISuitabilityTest) =>
                {
                    sign(createdTest?.id);
                    window.scrollTo(0, 0);
                },
                (requestError: unknown) =>
                {
                    reportRequestError(requestError);
                });
        }
    };

    const sign = (createdTestId?: number) =>
    {
        const identityId = userIdentity?.id;
        if(identityId !== undefined)
        {
            setIsLoaded(false);
            UserIdentitiesService.addSuitabilityTestSignatureToUserIdentity(identityId)
                .then((signedIdentity: UserIdentityType) =>
                {
                    // La firma va en el borrador de ese id, no en el último elemento del array.
                    const tests = signedIdentity?.suitabilityTests;
                    const draft = selectDraftSuitabilityTest(tests, createdTestId);
                    history.push(confirmUrl,
                    {
                        signatures: [draft?.signature ?? {}],
                        userIdentityId: identityId,
                        suitabilityTestId: draft?.id ?? createdTestId,
                    });
                },
                (requestError: unknown) =>
                {
                    reportRequestError(requestError);
                });
        }
    };

    const questionnaireReady = isLoaded && isLoadedSuitabilityTestVersion && userIdentity !== undefined;
    if(error.status && !questionnaireReady)
    {
        return <S.Error>{error.message}</S.Error>
    }

    return (
        <React.Fragment>
            <S.Cont>
                {(!isLoaded || !isLoadedSuitabilityTestVersion) && (<Spinner size={SpinnerSize.large} />)}
                {isLoaded && isLoadedSuitabilityTestVersion && (
                    <React.Fragment>
                        <S.MainCont>
                            {error.status && (
                                <S.Error>{error.message}</S.Error>
                            )}
                            <div className="mt20">
                                <h1>Test de idoneidad de la inversión</h1>
                            </div>
                            {userIdentity !== undefined && (
                                <>
                                    <S.SubTitle>
                                        Interviniente - <span>{[userIdentity.firstName, userIdentity.lastName, userIdentity.secondLastName].join(" ")}</span>
                                    </S.SubTitle>
                                    {!isAnswersLoaded && (
                                        <Spinner size={SpinnerSize.large} />
                                    )}
                                    {suitabilityTest !== undefined && questions !== undefined && suitabilityTestForm !== undefined && (
                                        <SuitabilityTestInputs
                                            suitabilityTestVersion={suitabilityTestVersion}
                                            suitabilityTest={suitabilityTest}
                                            handleSubmit={(event: any) => handleSubmit(event)}
                                            data={suitabilityTest}
                                            handleChange={(event: any, question: IQuestion) => handleTestChange(event, question)}
                                            formVal={suitabilityTestForm}
                                            setFormVal={(name: string, value: any) => updateSuitabilityTestForm(name, value)}
                                            questions={questions}
                                            canSubmit={canSubmit}
                                        />
                                    )}
                                </>
                            )}
                        </S.MainCont>
                    </React.Fragment>
                )}
            </S.Cont>
        </React.Fragment>
    );
};

export default SuitabilityTest;